import { useCallback, useEffect, useRef, useState } from 'react'
import { Aborted } from '../api/client'
import type { PageOf } from '../api/client'
import { subscribe } from '../api/events'
import { deuteFehler } from './queryError'

/**
 * Wie lange nach der letzten Änderung gewartet wird, bevor abgefragt wird.
 *
 * Ohne diese Pause löst jeder Buchstabe eine Abfrage aus — "NASDAQ" wären
 * sechs, von denen fünf verworfen werden. Die Pause gilt für JEDE Änderung und
 * nicht nur für ein bestimmtes Feld: Beim ersten Anlauf hing sie nur am
 * Suchfeld, und das Feld daneben feuerte ungebremst.
 */
const TYPING_PAUSE = 300

/**
 * Wie lange nach einem Ereignis gesammelt wird, bevor neu geladen wird.
 *
 * Trägt ein Agent zwanzig Meldungen ein, kämen zwanzig Hinweise. Ohne dieses
 * Fenster wären das zwanzig Abfragen für ein Ergebnis — dieselbe Überlegung
 * wie bei der Tipppause, mit einem anderen Auslöser.
 */
const EVENT_WINDOW = 500

export interface ListQuery<T> {
  result: PageOf<T> | null
  loading: boolean
  error: string | null
  /** Der Server hat mit 403 abgelehnt. */
  denied: boolean
  /**
   * Kennungen, die seit dem letzten Rendern dazukamen.
   *
   * Damit lässt sich eine neue Zeile hervorheben. Auf einem Bildschirm, den
   * niemand bedient, ist „was hat sich gerade geändert?“ die einzige Frage,
   * die zählt.
   */
  fresh: ReadonlySet<number>
  /**
   * Noch einmal laden, mit denselben Parametern.
   *
   * Für den Fall, dass die Liste sich geändert hat, ohne dass ein Parameter
   * sich änderte — nach dem Schreiben in der Watchlist. Ein neuer Eintrag
   * steht je nach Sortierung an anderer Stelle, ein ruhender fällt heraus;
   * die Antwort des Servers an die Stelle der alten Zeile zu setzen, träfe
   * beides nicht.
   */
  reload: () => void
}

export interface LiveOptions {
  /**
   * Kanal, auf den gehört wird. Kommt dort ein Hinweis, wird neu geladen —
   * mit DENSELBEN Parametern.
   *
   * Nicht die eine Zeile einsetzen: Ob sie in die gefilterte Liste gehört, wo
   * sie einzusortieren wäre und was aus der letzten Zeile der Seite wird,
   * weiß nur der Server. Der Client müsste Filter-, Sortier- und
   * Blätterlogik nachbilden.
   */
  topic?: string
}

/**
 * Lädt eine Liste und hält ihre Zustände.
 *
 * Steht hier und nicht in jeder Ansicht, weil es für jede Liste dasselbe ist:
 * laden, Fehler unterscheiden, 403 gesondert behandeln, den vorigen Aufruf
 * abbrechen, beim Tippen warten. Sechsmal abgeschrieben wäre es sechsmal zu
 * korrigieren, wenn etwas davon falsch ist.
 *
 * Die Parameter dienen zugleich als Auslöser: Ändert sich einer, wird neu
 * geladen. Verglichen wird ihr INHALT und nicht ihre Identität — ein Objekt
 * wechselt bei jedem Rendern die Identität, und die Liste liefe endlos.
 */
export function useListQuery<P extends object, T>(
  fetcher: (params: P, signal: AbortSignal) => Promise<PageOf<T>>,
  params: P,
  enabled: boolean = true,
  live: LiveOptions = {},
): ListQuery<T> {
  const [result, setResult] = useState<PageOf<T> | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const [denied, setDenied] = useState(false)
  const [fresh, setFresh] = useState<ReadonlySet<number>>(new Set())
  const first = useRef(true)

  // Ein Zähler als zusätzlicher Auslöser: Die Parameter ändern sich beim
  // Ereignis nicht, geladen werden soll trotzdem.
  const [tick, setTick] = useState(0)

  const key = JSON.stringify(params)

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()

    // Beim ersten Mal ohne Pause: Da hat niemand getippt, und 300 ms
    // Verzögerung wären beim Öffnen der Seite sichtbar.
    const wait = first.current ? 0 : TYPING_PAUSE
    first.current = false

    const timer = setTimeout(async () => {
      setLoading(true)
      setError(null)
      try {
        const answer = await fetcher(params, controller.signal)
        setResult(answer)
        setDenied(false)
      } catch (cause) {
        // Ein Abbruch ist kein Fehler: Er kam, weil schon die nächste Abfrage
        // läuft. Alles zurückzusetzen würde die Liste beim Tippen flackern
        // lassen.
        if (cause instanceof Aborted) return
        // Die Deutung steht in `queryError` — `useEndlessList` braucht
        // dieselbe, und zweimal geschrieben waere sie zweimal zu korrigieren.
        const deutung = deuteFehler(cause)
        setDenied(deutung.denied)
        setError(deutung.message)
        setResult(null)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }, wait)

    return () => { clearTimeout(timer); controller.abort() }
    // `key` statt `params`: der Inhalt entscheidet, nicht die Identität.
  }, [key, enabled, tick])   // eslint-disable-line react-hooks/exhaustive-deps

  // Der Ereignisstrom. Sammelt Hinweise und löst EINE Ladung aus.
  const topic = live.topic
  useEffect(() => {
    if (!enabled || !topic) return

    let timer: number | null = null
    const gesammelt = new Set<number>()

    const abmelden = subscribe(topic, (event) => {
      if (event.id !== null) gesammelt.add(event.id)
      if (timer !== null) return
      timer = window.setTimeout(() => {
        timer = null
        setFresh(new Set(gesammelt))
        gesammelt.clear()
        setTick((n) => n + 1)
      }, EVENT_WINDOW)
    })

    return () => {
      if (timer !== null) window.clearTimeout(timer)
      abmelden()
    }
  }, [topic, enabled])

  const reload = useCallback(() => setTick((n) => n + 1), [])

  return { result, loading, error, denied, fresh, reload }
}
